import { useState, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import api, { profileAPI } from "@/services/api";
import useUserStore from "@/stores/userStore";

const AvatarUpload = ({ userProfile, onAvatarUpdate }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      setSelectedImage(file);

      // Create preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    }
  };

  const handleSave = async () => {
    if (!selectedImage) return;

    setIsUploading(true);
    try {
      // Step 1: Upload image to Supabase Storage
      const avatarUrl = await uploadToSupabase(selectedImage);

      // Step 2: Update user profile with new avatar URL
      await profileAPI.updateProfile({
        avatar_url: avatarUrl,
      });

      // Step 3: Update global user store
      const updateUserAvatar = useUserStore.getState().updateAvatar;
      updateUserAvatar(avatarUrl);

      // Step 4: Call the callback to update parent state
      onAvatarUpdate(avatarUrl);

      // Step 5: Clean up
      setSelectedImage(null);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);

      alert("Avatar updated successfully!");
    } catch (error) {
      console.error("Failed to upload avatar:", error);
      alert("Failed to upload avatar. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleCancel = () => {
    setSelectedImage(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
  };

  const uploadToSupabase = async (file) => {
    try {
      const formData = new FormData();
      formData.append("avatar", file);

      const response = await api.post("/auth/upload-avatar", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      return response.data.avatarUrl;
    } catch (error) {
      console.error("Upload error:", error);
      throw new Error(error.response?.data?.error || "Upload failed");
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-4">
          {/* Avatar with click handler */}
          <div className="relative group" onClick={handleAvatarClick}>
            <Avatar className="w-16 h-16 cursor-pointer transition-all duration-200 group-hover:opacity-70">
              <AvatarImage
                src={previewUrl || userProfile.avatar_url}
                alt={userProfile.full_name}
              />
              <AvatarFallback className="bg-blue-100 text-blue-800 text-lg">
                {userProfile.username?.charAt(0).toUpperCase() || "U"}
              </AvatarFallback>
            </Avatar>

            {/* Camera overlay on hover */}
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer">
              <svg
                className="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
          </div>

          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">
              {userProfile.full_name}
            </h3>
            <p className="text-sm text-gray-600">@{userProfile.username}</p>

            {/* Upload controls */}
            {selectedImage && (
              <div className="flex items-center gap-2 mt-2">
                <Button size="sm" onClick={handleSave} disabled={isUploading}>
                  {isUploading ? "Uploading..." : "Save Avatar"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isUploading}
                >
                  Cancel
                </Button>
              </div>
            )}

            <div className="flex items-center gap-2 mt-2">
              <div className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                Active
              </div>
              <div className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                3 Instances
              </div>
            </div>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept="image/*"
          className="hidden"
        />
      </CardContent>
    </Card>
  );
};

export default AvatarUpload;
